import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0)
  const { t } = useTranslation()

  const faqs = [
    {
      question: t('faq.q1', "What is n8n?"),
      answer: t('faq.a1', "n8n is a powerful open-source workflow automation tool that connects your apps and automates repetitive tasks — no coding required. Think Zapier, but more powerful, flexible, and cost-effective at scale. It has 175,000+ stars on GitHub and is trusted by thousands of companies worldwide.")
    },
    {
      question: t('faq.q2', "How long does a typical project take?"),
      answer: t('faq.a2', "Simple workflow: 3-5 days. Multi-step complex automation: 1-2 weeks. Enterprise applications: 2-4 weeks. We'll give you a precise timeline after the discovery call, and we always meet our deadlines.")
    },
    {
      question: t('faq.q3', "Do I need to host n8n myself?"),
      answer: t('faq.a3', "No! We can work with n8n Cloud (their hosted version starting at $20/month) or set up self-hosting on your server. We'll recommend the best option based on your needs, budget, and data security requirements.")
    },
    {
      question: t('faq.q4', "What apps does it integrate with?"),
      answer: t('faq.a4', "Pretty much anything with an API. Common integrations include: HubSpot, Salesforce, Slack, Google Workspace, Notion, Airtable, Shopify, WooCommerce, Stripe, OpenAI, Mailchimp, SendGrid, and 400+ more tools. If it has an API, we can connect it.")
    },
    {
      question: t('faq.q5', "What if something breaks?"),
      answer: t('faq.a5', "All our packages include support. Additionally, we build error handling into every workflow so you're notified immediately if something needs attention. We also offer monthly maintenance plans for ongoing peace of mind.")
    },
    {
      question: t('faq.q6', "Can you work with our existing n8n setup?"),
      answer: t('faq.a6', "Absolutely! We can audit, optimize, or expand your current workflows. No need to start from scratch. Many clients come to us with broken or ineffective workflows that we fix and improve.")
    },
    {
      question: t('faq.q7', "How do I get started?"),
      answer: t('faq.a7', "Book a free 30-minute workflow audit. We'll hop on a call, learn about your operations, identify automation opportunities, and give you a clear roadmap. No pressure, no commitment — just actionable insights.")
    }
  ]

  return (
    <section id="faq" className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0a0a0f] relative">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('faq.badge', 'FAQ')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 sm:mt-4 mb-4 sm:mb-6 text-white">
            {t('faq.heading1', 'Frequently')} <span className="text-purple-400">{t('faq.heading2', 'asked')}</span> {t('faq.heading1c', 'questions')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('faq.description', 'Everything you need to know about working with us.')}
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index

            return (
              <motion.div 
                key={index}
                layout
                initial={false}
                className={`rounded-xl border ${
                  isOpen 
                    ? 'bg-[#1a1a2e] border-purple-500/30' 
                    : 'bg-white/5 border-white/10 hover:border-white/20'
                }`}
                transition={{
                  layout: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
                }}
              >
                <button
                  className="w-full flex items-center justify-between p-4 sm:p-6 text-left group"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                >
                  <span className={`font-semibold text-lg pr-4 transition-colors duration-300 ${
                    isOpen ? 'text-purple-400' : 'text-white group-hover:text-gray-200'
                  }`}>
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                    className={`flex-shrink-0 ${isOpen ? 'text-purple-400' : 'text-gray-400'}`}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </motion.div>
                </button>
                
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ 
                        height: { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] },
                        opacity: { duration: 0.25, delay: 0.05 }
                      }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-gray-400 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )
          })}
        </div>

        {/* Still have questions */}
        <div className="mt-12 text-center">
          <p className="text-gray-400">
            {t('faq.stillQuestions', 'Still have questions?')}{' '}
            <a href="https://cal.com/mostafa-elsheikh/30min" target="_blank" rel="noreferrer" className="text-purple-400 hover:underline font-medium">
              {t('faq.letsTalk', "Let's talk")}
            </a>
          </p>
        </div>
      </div>
    </section>
  )
}

export default FAQ